import { BugReport, NewReleases, Update, Email } from '@mui/icons-material';
import { Box, Stack, Typography, Button } from '@mui/material';
import { format } from 'date-fns';
import { IRelease, ReleaseType } from './types';
import { ShChip } from 'shared/SharedStyles/ShDataDisplay';
import { GlassPaper, FeatureRequestBox, ContactButton } from './Releases.styled';

const getTypeIcon = (type: ReleaseType) => {
    switch (type) {
        case 'feature':
            return <NewReleases fontSize="small" color="inherit" />;
        case 'improvement':
            return <Update fontSize="small" color="inherit" />;
        case 'bugfix':
            return <BugReport fontSize="small" color="inherit" />;
        default:
            return <NewReleases fontSize="small" color="inherit" />;
    }
};

const getTypeColor = (type: ReleaseType) => {
    switch (type) {
        case 'feature':
            return '#2ecc71'; // Emerald green
        case 'improvement':
            return '#3498db'; // Sky blue
        case 'bugfix':
            return '#e74c3c'; // Coral red
        default:
            return '#9b59b6'; // Amethyst purple
    }
};

interface ReleasesListProps {
    releases: IRelease[];
}

export const ReleasesList = ({ releases }: ReleasesListProps) => {
    return (
        <Stack spacing={4}>
            <FeatureRequestBox>
                <Stack spacing={2}>
                    <Typography variant="h6" color="primary">
                        Have a Feature Request or Found a Bug?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        We'd love to hear from you! Send us an email at{' '}
                        <Typography
                            component="a"
                            href="mailto:admin@smoothhiring.com"
                            color="primary"
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                            admin@smoothhiring.com
                        </Typography>
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Email />}
                        href="mailto:admin@smoothhiring.com"
                        sx={ContactButton}
                    >
                        Contact Us
                    </Button>
                </Stack>
            </FeatureRequestBox>

            <Stack spacing={3}>
                {releases.map((release) => (
                    <GlassPaper
                        key={release.id}
                        elevation={0}
                        sx={{
                            p: 3,
                            borderRadius: 2,
                        }}
                    >
                        <Stack spacing={2}>
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                flexWrap="wrap"
                            >
                                <Typography variant="body1">
                                    {release.title}
                                </Typography>
                                <ShChip
                                    borderRadius="15px"
                                    icon={getTypeIcon(release.type)}
                                    label={release.type}
                                    bgColor={getTypeColor(release.type)}
                                    textColor="white"
                                    size="small"
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {format(new Date(release.date), 'MMM dd, yyyy')}
                                </Typography>
                            </Stack>

                            <Typography variant="body2" color="text.secondary">
                                {release.description}
                            </Typography>

                            {release.changes && (
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                    {release.changes.map((change, changeIndex) => (
                                        <Typography
                                            key={changeIndex}
                                            component="li"
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {change}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Stack>
                    </GlassPaper>
                ))}
            </Stack>
        </Stack>
    );
}; 